import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Wifi, Coffee, Briefcase, ArrowRight, Heart } from "lucide-react";

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      ref={ref}
      className="min-h-screen bg-[#F5ECD7] py-24 md:py-32 scroll-mt-20"
    >
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        
        {/* The Elephant in the Room */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#7A9E7E] mb-6 block">
            The Honest Part
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#2C2C2C] leading-[1.1] mb-8">
            "Wait, you charge?"
            <br />
            <span className="italic text-[#C17A54]">Yeah. Here's why.</span>
          </h2>
        </motion.div>

        {/* The Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16 md:mb-24">
          
          {/* Left - The Why */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <p className="font-sans text-lg md:text-xl text-[#2C2C2C]/70 leading-relaxed">
              We're not a company. No investors. No board meetings. Just two guys 
              who quit their jobs to do this full-time.
            </p>
            
            {/* Visual - Left Jobs */}
            <div className="flex items-center gap-3 bg-[#E8A838]/10 px-5 py-4 rounded-2xl border border-[#E8A838]/20">
              <div className="w-10 h-10 rounded-full bg-[#E8A838]/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#E8A838]" />
              </div>
              <div>
                <p className="font-sans text-sm text-[#2C2C2C] font-medium">Left our 9-to-5s</p>
                <p className="font-sans text-xs text-[#2C2C2C]/50">To be there for people, fully</p>
              </div>
            </div>

            <p className="font-sans text-lg md:text-xl text-[#2C2C2C]/70 leading-relaxed">
              The small fee? It keeps the lights on—literally.
            </p>
          </motion.div>

          {/* Right - Where it goes */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <p className="font-sans text-sm text-[#2C2C2C]/50 mb-4">Where your $ goes:</p>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-white border border-[#7A9E7E]/20 rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <Wifi className="w-5 h-5 text-[#7A9E7E]" />
                  <span className="font-sans text-[#2C2C2C]">Platform & hosting</span>
                </div>
                <span className="font-sans text-sm text-[#2C2C2C]/40">servers, calls, tools</span>
              </div>
              
              <div className="flex items-center justify-between bg-white border border-[#C17A54]/20 rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <Coffee className="w-5 h-5 text-[#C17A54]" />
                  <span className="font-sans text-[#2C2C2C]">Our coffee fund</span>
                </div>
                <span className="font-sans text-sm text-[#2C2C2C]/40">gotta stay caffeinated</span>
              </div>
              
              <div className="flex items-center justify-between bg-white border border-[#E8A838]/20 rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-[#E8A838]" />
                  <span className="font-sans text-[#2C2C2C]">Keeping this alive</span>
                </div>
                <span className="font-sans text-sm text-[#2C2C2C]/40">so we can keep showing up</span>
              </div>
            </div>

            <p className="font-serif italic text-[#2C2C2C]/50 mt-6 text-center">
              That's it. No profit margins. No fancy offices.
            </p>
          </motion.div>
        </div>

        {/* The Pricing Card */}
        <motion.div
          id="book"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-2xl mx-auto scroll-mt-24"
        >
          <div className="bg-[#2C2C2C] rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#7A9E7E]/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#C17A54]/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <p className="font-sans text-[#F5ECD7]/60 text-sm mb-4">A conversation with us</p>
              
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="font-serif text-6xl md:text-7xl text-[#F5ECD7]">$50</span>
                <span className="font-sans text-xl text-[#E8A838]">/ hour</span>
              </div>
              
              <p className="font-sans text-[#F5ECD7]/40 text-sm mb-8">
                We need at least an hour to know you.
              </p>

              {/* CTA */}
              <a
                href="https://calendly.com/faiyaz-alongside/alongside"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-cta"
                className="inline-flex items-center gap-3 bg-[#E8A838] text-[#2C2C2C] font-serif italic text-lg px-8 py-4 rounded-full hover:bg-[#F5ECD7] transition-all duration-300 group"
              >
                Can we know you?
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>

              <p className="font-sans text-xs text-[#F5ECD7]/30 mt-6">
                Book a slot, and we'll be there.
              </p>
            </div>
          </div>

          {/* Bottom note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center font-serif italic text-[#2C2C2C]/50 mt-8"
          >
            No subscriptions. No commitments. Just show up when you need to.
          </motion.p>
        </motion.div>

      </div>
    </section>
  );
};
